import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Input from '../../../components/ui/Input';
import Button from '../../../components/ui/Button';
import { leadService } from '../../../services/leadService';
import { trackEvent } from '../../../hooks/useGoogleAnalytics';
import { trackEnquirySubmit, trackLeadConfirmed, trackLeadAsConversion,  } from '../../../utils/analytics';

const ComprehensiveLeadForm = () => {
  const [formData, setFormData] = useState({
    fullName: '',
    businessEmail: '',
    phone: '',
    companyName: '',
    companyType: '',
    serviceInterest: '',
    numberOfLocations: '',
    budgetRange: '',
    message: ''
  });
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');

  const companyTypes = [
  'Manufacturing',
  'IT/Software',
  'Retail',
  'Healthcare',
  'Education',
  'Hospitality',
  'Real Estate',
  'Logistics',
  'BFSI',
  'Other'];


  const serviceInterests = [
  'Facility Management',
  'Housekeeping Services',
  'Security Services',
  'Pantry Services',
  'Technical Maintenance',
  'Landscaping',
  'Pest Control',
  'Complete Operations Management',
  'Other'];


  const budgetRanges = [
  'Under ₹5 Lakhs/month',
  '₹5-10 Lakhs/month',
  '₹10-25 Lakhs/month',
  '₹25-50 Lakhs/month',
  'Above ₹50 Lakhs/month'];


  const handleChange = (e) => {
    const { name, value } = e?.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    setLoading(true);
    setError('');
    setSuccess(false);

    // Funnel Stage 5: Enquiry submitted
    trackEnquirySubmit('Comprehensive Enquiry', 'home_lead_form', formData, {
      company_type: formData?.companyType,
      service_interest: formData?.serviceInterest,
    });

    try {
      // Prepare lead data for email notification
      const leadEmailData = {
        fullName: formData?.fullName,
        businessEmail: formData?.businessEmail,
        phone: formData?.phone,
        companyName: formData?.companyName,
        companyType: formData?.companyType,
        serviceInterest: formData?.serviceInterest,
        numberOfLocations: formData?.numberOfLocations,
        budgetRange: formData?.budgetRange,
        message: formData?.message,
        source: 'Get a Free Consultation Form'
      };

      // Save to database first
      await leadService?.create({
        name: formData?.fullName,
        email: formData?.businessEmail,
        phone: formData?.phone,
        company: formData?.companyName,
        serviceRequired: formData?.serviceInterest,
        message: `Company Type: ${formData?.companyType}\nLocations: ${formData?.numberOfLocations}\nBudget: ${formData?.budgetRange}\n\nMessage: ${formData?.message}`,
        source: 'comprehensive_enquiry',
        status: 'new'
      });

      // Send email notification to admins and partners
      try {
        const { emailService } = await import('../../../services/emailService');
        await emailService?.sendLeadNotification(leadEmailData);
      } catch (emailError) {
        console.error('Email notification failed (non-fatal):', emailError);
      }

      trackEvent('comprehensive_lead_generated', {
        source: 'comprehensive_enquiry',
        companyType: formData?.companyType,
        service: formData?.serviceInterest,
        budgetRange: formData?.budgetRange
      });

      // Funnel Stage 6: Lead confirmed + conversion value
      trackLeadConfirmed(formData?.serviceInterest, 'comprehensive_enquiry', {
        company_type: formData?.companyType,
      });
      trackLeadAsConversion(formData?.serviceInterest, formData?.budgetRange, 'comprehensive_enquiry');

      setSuccess(true);
      setFormData({
        fullName: '',
        businessEmail: '',
        phone: '',
        companyName: '',
        companyType: '',
        serviceInterest: '',
        numberOfLocations: '',
        budgetRange: '',
        message: ''
      });

      setTimeout(() => setSuccess(false), 5000);
    } catch (err) {
      setError(err?.message || 'Failed to submit enquiry. Please try again.');
      console.error('Lead generation error:', err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <section className="py-12 md:py-16 lg:py-20 px-4 md:px-6 lg:px-8 bg-gradient-to-br from-primary/5 to-secondary/5">
      <div className="max-w-6xl mx-auto">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12">
          {/* Left Side - Form Information */}
          <div>
            <div className="mb-8">
              <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4">
                Ready to Transform Your Operations?
              </h2>
              <p className="text-lg text-muted-foreground mb-6">
                Get in touch with our experts to discuss your business needs and discover how ZippyAssist can help you achieve operational excellence.
              </p>
            </div>

            <div className="space-y-4 mb-8">
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 rounded-lg bg-primary flex items-center justify-center flex-shrink-0">
                  <Icon name="Phone" size={20} className="text-white" />
                </div>
                <div>
                  <h4 className="font-semibold text-foreground mb-1">Phone Support</h4>
                  <p className="text-sm text-muted-foreground">+91 9372131154</p>
                </div>
              </div>

              <div className="flex items-start gap-3">
                <div className="w-10 h-10 rounded-lg bg-secondary flex items-center justify-center flex-shrink-0">
                  <Icon name="Mail" size={20} className="text-white" />
                </div>
                <div>
                  <h4 className="font-semibold text-foreground mb-1">Email Support</h4>
                  <p className="text-sm text-muted-foreground">business@obasegroup.com</p>
                </div>
              </div>

              <div className="flex items-start gap-3">
                <div className="w-10 h-10 rounded-lg bg-green-600 flex items-center justify-center flex-shrink-0">
                  <Icon name="MessageCircle" size={20} className="text-white" />
                </div>
                <div>
                  <h4 className="font-semibold text-foreground mb-1">WhatsApp Support</h4>
                  <a href="https://wa.me/917039149001" target="_blank" rel="noopener noreferrer" className="text-sm text-muted-foreground hover:text-primary transition-colors">+91 7039149001</a>
                </div>
              </div>

              <div className="flex items-start gap-3">
                <div className="w-10 h-10 rounded-lg bg-accent flex items-center justify-center flex-shrink-0">
                  <Icon name="MapPin" size={20} className="text-white" />
                </div>
                <div>
                  <h4 className="font-semibold text-foreground mb-1">Response Time</h4>
                  <p className="text-sm text-muted-foreground">We'll contact you within 24 hours</p>
                </div>
              </div>
            </div>

            <div className="bg-card rounded-xl p-6 border border-border">
              <h4 className="font-bold text-foreground mb-3">Why Choose Zippy Assist?</h4>
              <ul className="space-y-2">
                <li className="flex items-center gap-2 text-sm text-muted-foreground">
                  <Icon name="Check" size={16} className="text-success" />
                  <span>500+ Enterprises Trust Us</span>
                </li>
                <li className="flex items-center gap-2 text-sm text-muted-foreground">
                  <Icon name="Check" size={16} className="text-success" />
                  <span>PAN India Service Coverage</span>
                </li>
                <li className="flex items-center gap-2 text-sm text-muted-foreground">
                  <Icon name="Check" size={16} className="text-success" />
                  <span>24×7 Dedicated Support</span>
                </li>
                <li className="flex items-center gap-2 text-sm text-muted-foreground">
                  <Icon name="Check" size={16} className="text-success" />
                  <span>Trusted by B&S Venture Pvt. Ltd.</span>
                </li>
              </ul>
            </div>
          </div>

          {/* Right Side - Form */}
          <div className="bg-card rounded-2xl shadow-xl border border-border p-6 md:p-8">
            <div className="text-center mb-6">
              <div className="inline-flex items-center justify-center w-14 h-14 bg-primary rounded-full mb-3">
                <Icon name="Send" size={24} className="text-white" />
              </div>
              <h3 className="text-2xl font-bold text-foreground mb-2">Get a Free Consultation</h3>
              <p className="text-sm text-muted-foreground">Fill out the form and we'll be in touch soon</p>
            </div>

            {success &&
            <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-lg flex items-start gap-3">
                <Icon name="CheckCircle" size={20} className="text-green-600 mt-0.5" />
                <div>
                  <h4 className="font-semibold text-green-900 mb-1">Business Enquiry Submitted!</h4>
                  <p className="text-sm text-green-700">Our team will contact you within 24 hours.</p>
                </div>
              </div>
            }

            {error &&
            <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg flex items-start gap-3">
                <Icon name="AlertCircle" size={20} className="text-red-600 mt-0.5" />
                <div>
                  <h4 className="font-semibold text-red-900 mb-1">Submission Failed</h4>
                  <p className="text-sm text-red-700">{error}</p>
                </div>
              </div>
            }

            <form onSubmit={handleSubmit} className="space-y-5">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">
                    Full Name <span className="text-destructive">*</span>
                  </label>
                  <Input
                    type="text"
                    name="fullName"
                    value={formData?.fullName}
                    onChange={handleChange}
                    placeholder="Enter your name"
                    required />

                </div>

                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">
                    Business Email <span className="text-destructive">*</span>
                  </label>
                  <Input
                    type="email"
                    name="businessEmail"
                    value={formData?.businessEmail}
                    onChange={handleChange}
                    placeholder="you@company.com"
                    required />

                </div>

                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">
                    Phone Number <span className="text-destructive">*</span>
                  </label>
                  <Input
                    type="tel"
                    name="phone"
                    value={formData?.phone}
                    onChange={handleChange}
                    placeholder="+91 XXXXX XXXXX"
                    required />

                </div>

                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">
                    Company Name <span className="text-destructive">*</span>
                  </label>
                  <Input
                    type="text"
                    name="companyName"
                    value={formData?.companyName}
                    onChange={handleChange}
                    placeholder="Your company"
                    required />

                </div>

                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">
                    Company Type <span className="text-destructive">*</span>
                  </label>
                  <select
                    name="companyType"
                    value={formData?.companyType}
                    onChange={handleChange}
                    required
                    className="w-full h-10 px-3 py-2 border border-input rounded-md bg-background text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-ring">

                    <option value="">Select type</option>
                    {companyTypes?.map((type) =>
                    <option key={type} value={type}>{type}</option>
                    )}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">
                    Service Interest <span className="text-destructive">*</span>
                  </label>
                  <select
                    name="serviceInterest"
                    value={formData?.serviceInterest}
                    onChange={handleChange}
                    required
                    className="w-full h-10 px-3 py-2 border border-input rounded-md bg-background text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-ring">

                    <option value="">Select service</option>
                    {serviceInterests?.map((service) =>
                    <option key={service} value={service}>{service}</option>
                    )}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">
                    Number of Locations
                  </label>
                  <Input
                    type="text"
                    name="numberOfLocations"
                    value={formData?.numberOfLocations}
                    onChange={handleChange}
                    placeholder="e.g., 5 locations" />

                </div>

                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">
                    Budget Range
                  </label>
                  <select
                    name="budgetRange"
                    value={formData?.budgetRange}
                    onChange={handleChange}
                    className="w-full h-10 px-3 py-2 border border-input rounded-md bg-background text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-ring">

                    <option value="">Select budget</option>
                    {budgetRanges?.map((range) =>
                    <option key={range} value={range}>{range}</option>
                    )}
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-foreground mb-2">
                  Message / Requirements <span className="text-destructive">*</span>
                </label>
                <textarea
                  name="message"
                  value={formData?.message}
                  onChange={handleChange}
                  placeholder="Tell us about your requirements..."
                  rows={4}
                  required
                  className="w-full px-4 py-3 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary bg-background text-foreground text-sm" />

              </div>

              <Button
                type="submit"
                size="lg"
                className="w-full"
                disabled={loading}
                iconName={loading ? 'Loader' : 'Send'}
                iconPosition="left">

                {loading ? 'Submitting...' : 'Send Business Enquiry'}
              </Button>
            </form>
          </div>
        </div>
      </div>
    </section>);

};

export default ComprehensiveLeadForm;